import {
    Link,
    Select,
    SpaceBetween,
    TextContent
} from '@amzn/awsui-components-react';
import React, { useMemo } from 'react';
import { LOG_GROUP_PREFIX } from '../hooks/useWorkflow';
import { WorkflowExecutionInfo } from '../../../../types';

const getConsoleUrl = (arn: string): string => {
    try {
        const region = arn.split(':')[3];
        return `https://console.aws.amazon.com/states/home?region=${region}#/executions/details/${arn}`;
    } catch (error) {
        console.error(error);
        return '#';
    }
};

const getExecutionLabel = (info: WorkflowExecutionInfo) => {
    const resourceId = info.arn.split(':').pop() || info.arn;
    const logGroup = info.logGroup.replace(LOG_GROUP_PREFIX, '');
    return `${logGroup}: ${resourceId}`;
}

interface Props {
    selected: string;
    executions: WorkflowExecutionInfo[];
    setExecution: (execution: string) => void;
}

const ExecutionSelector: React.FC<Props> = ({
    selected,
    executions,
    setExecution,
}) => {
    const options = useMemo(() => [
        { value: '', label: 'All executions' },
        ...executions.map(info => ({
            value: info.arn,
            label: getExecutionLabel(info)
        }))
    ], [executions]);

    const selectedOption = useMemo(() => {
        if (!selected) {
            return { value: '', label: 'All executions' };
        }
        
        const selectedInfo = executions.find(info => info.arn === selected);
        if (selectedInfo) {
            return {
                value: selected,
                label: getExecutionLabel(selectedInfo)
            };
        }
        
        return { value: selected, label: selected.split(':').pop() || selected };
    }, [selected, executions]);

    return (
        <SpaceBetween direction="vertical" size="xs">
            <TextContent>Workflow Executions:</TextContent>
            <Select
                selectedOption={selectedOption}
                onChange={({ detail }) => setExecution(detail.selectedOption?.value || '')}
                options={options}
                placeholder="Select execution"
                empty="No executions available"
            />

            {selected && (
                <Link
                    external
                    href={getConsoleUrl(selected)}
                >
                    View in Console
                </Link>
            )}
        </SpaceBetween>
    );
};

export default ExecutionSelector;
